import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import {
  api_private_get,
  api_private_post,
  formatStringAmountCredits,
  showAlert,
} from "script.js";
import useBet from "context/useBet";
import useAuth from "context/useAuth";
import "./OrderForm.css";

function parseAmountInput(value) {
  const parsedValue = parseFloat(value);
  if (isNaN(parsedValue) || parsedValue < 0) {
    console.warn("Invalid input for bet amount:", value);
    return null;
  }
  const roundedValue = Math.round(parsedValue * 100) / 100;
  return roundedValue;
}

const OrderForm = () => {
  const { selectedBet, setSelectedBet } = useBet();
  const { user, refreshUser } = useAuth();
  const amountInputRef = useRef(null);

  const [slipAmount, setSlipAmount] = useState("0.00");
  const [slipData, setSlipData] = useState({
    number: 1,
    category: "Empty",
    displayName: "display name",
    fullName: "full name",
  });

  const [slipOdds, setSlipOdds] = useState("4.321");
  const [slipDisabled, setSlipDisabled] = useState(false);
  const [showConfirmBtns, setShowConfirmBtns] = useState(false);
  const [sliderPercentage, setSliderPercentage] = useState("0");
  const [activeAmountButton, setActiveAmountButton] = useState(null);

  // Derived winnings
  const parsedAmount = parseAmountInput(slipAmount) ?? 0;
  const parsedOdds = parseFloat(slipOdds);
  const balance = Math.max(0, Number(user?.balance) || 0);
  const configuredLimit = user?.effective_prediction_limit;
  const maxAmount = Math.max(
    0,
    configuredLimit === null || configuredLimit === undefined
      ? balance
      : Math.min(balance, Number(configuredLimit)),
  );
  const maxAmountLabel = maxAmount.toFixed(2);
  const slipWinAmount = !isNaN(parsedOdds)
    ? (Math.floor(parsedAmount * parsedOdds * 100) / 100).toFixed(2)
    : "0.00";
  const maxAmountDisplay = formatStringAmountCredits(
    maxAmountLabel,
    user?.account_type,
  );
  const slipAmountDisplay = formatStringAmountCredits(
    parsedAmount.toFixed(2),
    user?.account_type,
  );
  const slipWinAmountDisplay = formatStringAmountCredits(
    slipWinAmount,
    user?.account_type,
  );

  // Effect for fetching/updating odds
  useEffect(() => {
    if (selectedBet === null) return;

    api_private_get("/market/getDisplayOdds", (data) => {
      const betData = data.find((bet) => bet.product_id === selectedBet);
      if (!betData) {
        showAlert("Bet data not found.", "danger");
        setSlipDisabled(false);
        setSelectedBet(null);
        return;
      }

      setSlipData((prev) => ({
        ...prev,
        fullName: betData.name,
        displayName: betData.name.replace(/-/g, " "),
        category: "Crypto HOT",
      }));

      setSlipOdds(parseFloat(betData.last_price).toFixed(3));
      setSlipDisabled(false);
    });

    const interval = setInterval(() => {
      api_private_get("/market/getDisplayOdds", (data) => {
        const betData = data.find((bet) => bet.product_id === selectedBet);
        if (!betData) {
          showAlert("Bet data not found.", "danger");
          setSlipDisabled(false);
          setSelectedBet(null);
          return;
        }

        const newOdds = parseFloat(betData.last_price).toFixed(3);
        setSlipOdds((prev) => {
          if (newOdds !== prev) {
            setShowConfirmBtns(false); // only reset confirm buttons if odds actually changed
          }
          return newOdds === prev ? prev : newOdds;
        });
      });
    }, 1000);

    return () => clearInterval(interval);
  }, [selectedBet, setSelectedBet]);

  const handleClose = () => {
    setSelectedBet(null);
    setShowConfirmBtns(false);
  };

  const handleAmountBlur = () => {
    const parsedAmount = parseAmountInput(slipAmount);
    if (parsedAmount === null) {
      setSlipAmount("0.00");
    } else {
      setSlipAmount(parsedAmount.toFixed(2));
    }
  };

  const handleAmountInputChange = (event) => {
    setShowConfirmBtns(false);
    setActiveAmountButton(null);
    setSlipAmount(event.target.value);
  };

  const handleMaxButtonClick = () => {
    setShowConfirmBtns(false);
    setActiveAmountButton(100);
    setSlipAmount(maxAmountLabel);
    setSliderPercentage("100");
  };

  const handleSliderChange = (event) => {
    setShowConfirmBtns(false);
    setActiveAmountButton(null);
    setSliderPercentage(event.target.value);
    const betAmount = ((maxAmount * event.target.value) / 100).toFixed(2);
    setSlipAmount(betAmount);
  };

  const handleAmountBtnsClick = (percentage) => {
    setShowConfirmBtns(false);
    setActiveAmountButton(percentage);
    const betAmount = ((maxAmount * percentage) / 100).toFixed(2);
    setSlipAmount(betAmount);
    setSliderPercentage(percentage.toString());
  };

  const handleFirstConfirmPress = () => {
    const parsedAmount = parseAmountInput(slipAmount);
    if (parsedAmount === null || parsedAmount <= 0) {
      showAlert("Please enter a valid bet amount greater than zero.", "danger");
      setSlipAmount("");
      amountInputRef.current?.focus();
      return;
    }
    if (parsedAmount > maxAmount) {
      showAlert(`The maximum stake is ${maxAmountDisplay}.`, "danger");
      setSlipAmount(maxAmountLabel);
      amountInputRef.current?.focus();
      return;
    }
    setShowConfirmBtns(true);
  };

  const handleConfirmOrder = () => {
    setShowConfirmBtns(false);
    api_private_post(
      "/market/executeBet",
      {
        betName: slipData.fullName,
        amount: parseFloat(slipAmount),
        odds: parseFloat(slipOdds),
      },
      (response) => {
        if (response.success) {
          showAlert(
            `Order ${response.data.order_id} placed successfully!`,
            "success",
          );
          void refreshUser();
          setSlipDisabled(true);
          setSelectedBet(null);
          handleClose();
        } else {
          showAlert("Failed to place order: " + response.error, "danger");
        }
      },
    );
  };

  return (
    <>
      <div
        className={`order-form-overlay ${selectedBet !== null ? "active" : ""}`}
        onClick={handleClose}
      />

      <div
        className={`order-form-panel ${selectedBet !== null ? "open" : ""} ${slipDisabled ? "disabled-element" : ""}`}
      >
        <div className="order-form-header">
          <h2 className="order-form-title">New Order</h2>
          <button className="order-form-close" onClick={handleClose}>
            <i className="ti ti-x"></i>
          </button>
        </div>

        <div className="order-form-content">
          {selectedBet === null ? (
            <div className="order-empty-state">
              <div className="order-empty-icon">
                <i className="ti ti-inbox"></i>
              </div>
              <div className="order-empty-text">
                Select a contract from the market to create a new order
              </div>
            </div>
          ) : (
            <>
              {/* Market Info */}
              <div className="order-market-info">
                <div className="order-market-header">
                  <div className="order-market-symbol">
                    <div className="order-market-symbol-badge">ETH</div>
                    <div className="order-market-name">
                      <div className="order-market-name-main">
                        {slipData.displayName}
                      </div>
                      <div className="order-market-name-sub">
                        {slipData.fullName}
                      </div>
                    </div>
                  </div>
                  <div className="order-odds-display">
                    <div className="order-odds-value">{slipOdds}</div>
                    <div className="order-odds-label">Current Odds</div>
                  </div>
                </div>
                <div
                  style={{
                    marginTop: "12px",
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                  }}
                >
                  <span className="order-category-tag">
                    {slipData.category}
                  </span>
                  <span
                    style={{ fontSize: "11px", color: "rgba(var(--n5), 0.7)" }}
                  >
                    Index price in USD
                  </span>
                </div>
              </div>

              {/* Stake Input */}
              <div className="order-form-group">
                <label className="order-form-label">Stake Amount</label>
                <div className="order-form-input-wrapper">
                  <input
                    className="order-form-input"
                    type="text"
                    placeholder="0.00"
                    ref={amountInputRef}
                    value={slipAmount}
                    onChange={handleAmountInputChange}
                    onBlur={handleAmountBlur}
                  />
                  <button
                    className="order-max-btn"
                    onClick={handleMaxButtonClick}
                  >
                    Max
                  </button>
                </div>
                <div
                  style={{ fontSize: "12px", color: "rgba(var(--n5), 0.8)" }}
                >
                  Maximum available: {maxAmountDisplay}
                </div>
              </div>

              {/* Range Slider */}
              <div className="order-range-container">
                <input
                  className="order-range-slider"
                  type="range"
                  min="0"
                  max="100"
                  value={sliderPercentage}
                  onChange={handleSliderChange}
                />
              </div>

              {/* Quick Select Buttons */}
              <div className="order-form-group">
                <label className="order-form-label">Quick Select</label>
                <div className="order-percent-buttons">
                  <button
                    className={`order-percent-btn ${activeAmountButton === 25 ? "active" : ""}`}
                    onClick={() => handleAmountBtnsClick(25)}
                  >
                    25%
                  </button>
                  <button
                    className={`order-percent-btn ${activeAmountButton === 50 ? "active" : ""}`}
                    onClick={() => handleAmountBtnsClick(50)}
                  >
                    50%
                  </button>
                  <button
                    className={`order-percent-btn ${activeAmountButton === 75 ? "active" : ""}`}
                    onClick={() => handleAmountBtnsClick(75)}
                  >
                    75%
                  </button>
                  <button
                    className={`order-percent-btn ${activeAmountButton === 100 ? "active" : ""}`}
                    onClick={() => handleAmountBtnsClick(100)}
                  >
                    100%
                  </button>
                </div>
              </div>

              {/* Summary */}
              <div className="order-summary">
                <div className="order-summary-row">
                  <span className="order-summary-label">Stake</span>
                  <span className="order-summary-value">
                    {slipAmountDisplay}
                  </span>
                </div>
                <div className="order-summary-row">
                  <span className="order-summary-label">Odds</span>
                  <span className="order-summary-value">x{slipOdds}</span>
                </div>
                <div className="order-summary-divider"></div>
                <div className="order-summary-row order-summary-total">
                  <span className="order-summary-label">Potential Payout</span>
                  <span className="order-summary-total-value">
                    {slipWinAmountDisplay}
                  </span>
                </div>
              </div>

              {/* Actions */}
              <div className="order-actions">
                {showConfirmBtns ? (
                  <>
                    <button
                      className="order-action-btn order-confirm-btn"
                      onClick={handleConfirmOrder}
                    >
                      Place Order
                    </button>
                    <button
                      className="order-action-btn order-cancel-btn"
                      onClick={() => setShowConfirmBtns(false)}
                    >
                      Cancel
                    </button>
                  </>
                ) : !user ? (
                  <Link to="/login" style={{ textDecoration: "none" }}>
                    <button
                      className="order-action-btn order-login-btn"
                      type="button"
                    >
                      Log In to Trade
                    </button>
                  </Link>
                ) : (
                  <button
                    className="order-action-btn order-confirm-btn"
                    onClick={handleFirstConfirmPress}
                  >
                    Review Order
                  </button>
                )}
              </div>
            </>
          )}
        </div>
      </div>
    </>
  );
};

export default OrderForm;
